import { AgeGroup } from './age_range';

export interface ResolveAgeGroupOptions {
  dateOfBirth: Date | string;
  now: Date;
  config: AgeGroup[] | null;
}

export function resolveAgeGroup({
  dateOfBirth,
  now,
  config,
}: ResolveAgeGroupOptions): AgeGroup | null {
  if (!config || config.length === 0) {
    return null;
  }

  // Normalizar la fecha de nacimiento a objeto Date
  let birthDate: Date;

  if (typeof dateOfBirth === 'string' && dateOfBirth.trim() !== '') {
    birthDate = new Date(dateOfBirth);
    // Validar que la fecha string sea válida
    if (isNaN(birthDate.getTime())) {
      return null;
    }
  } else if (dateOfBirth instanceof Date) {
    // Validar que la fecha Date sea válida
    if (isNaN(dateOfBirth.getTime())) {
      return null;
    }

    birthDate = dateOfBirth;
  } else {
    return null;
  }

  // Calcular la edad en años usando diferencia de fechas más precisa
  const ageInYears = calculateAgeInYears(birthDate, now);

  // Buscar la configuración que cumpla las condiciones (ordenada por maxAge ascendente)
  const rv = config.find((x) => ageInYears <= x.maxAge);
  return rv ?? null;
}

/**
 * Calcula la edad en años de forma más precisa considerando el día y mes exacto
 */
function calculateAgeInYears(birthDate: Date, currentDate: Date): number {
  let age = currentDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = currentDate.getMonth() - birthDate.getMonth();

  // Ajustar si aún no ha llegado el cumpleaños este año
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && currentDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

export function resolveAgeGroupFromSnapshot(
  type: string,
  config: AgeGroup[]
): AgeGroup | null {
  const rv: AgeGroup | undefined = config?.find((x) => x.type === type);
  return rv ?? null;
}

/**
 * Etiqueta de display del grupo etario de una bolsa producida, resuelta a
 * partir del snapshot histórico `ageGroupType` que quedó grabado en la
 * prescripción (no se recalcula con los datos actuales del paciente).
 *
 * El asterisco marca que el peso del paciente supera el máximo del grupo — es
 * la convención que ya usaban los reportes de producción, y vive acá para que
 * el detalle de bolsas y el de remitos muestren siempre lo mismo.
 */
export const NO_AGE_GROUP_LABEL = 'S/N';

export function resolveAgeGroupLabel(
  type: string,
  weight: number,
  config: AgeGroup[]
): string {
  const ageGroup = resolveAgeGroupFromSnapshot(type, config);

  if (!ageGroup) {
    return NO_AGE_GROUP_LABEL;
  }

  return weight > ageGroup.maxWeight ? `${ageGroup.name} *` : ageGroup.name;
}
